import React, { useState, useEffect, CSSProperties } from "react";

type TabVariant = "command" | "alternate";

const getHeaderStyle = (
  externalStyle: CSSProperties,
  active: boolean,
  height: number,
  variant: TabVariant,
  headerWidth: number | undefined | "auto",
  canBeClicked: boolean
) => {
  let borderActiveColor = "white";
  let color = "white";

  switch (variant) {
    case "command":
      borderActiveColor = "white";
      break;
    case "alternate":
      borderActiveColor = "#0059CC";
      color = "#0059CC";
      break;
  }

  return {
    flexGrow: 1,
    flexBasis: 0,
    height: height,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    color: active ? color : "inherit",
    borderBottom: active ? `4px solid ${borderActiveColor}` : "",
    margin: "0px 5px 0px 5px",
    cursor: canBeClicked ? "pointer" : "default",
    width: headerWidth ?? "auto",
    ...externalStyle,
  };
};

const getHeaderContainerStyle = (variant: TabVariant, fitContent: boolean) => {
  let color = "#FFFFFF";
  let backgroundColor = "#414042";
  let borderBottomColor = "white";
  let fontSize = 20;

  switch (variant) {
    case "command":
      color = "#FFFFFF";
      backgroundColor = "#414042";
      borderBottomColor = "white";
      fontSize = 20;
      break;
    case "alternate":
      color = "#B4B4B4";
      backgroundColor = "transparent";
      borderBottomColor = "#414042";
      fontSize = 16;
      break;
  }

  return {
    display: "flex",
    alignItems: "center",
    fontSize: fontSize,
    color: color,
    backgroundColor: backgroundColor,
    borderBottom: `1px solid ${borderBottomColor}`,
    width: fitContent ? "fit-content" : "auto",
  };
};

type Props = {
  id: string;
  children: { [index: string]: React.ReactNode };
  style?: React.CSSProperties;
  collapsible: boolean;
  defaultSelectedIndex?: number;
  allowChange?: boolean;
  disabled?: boolean;
  activeTabStyle?: React.CSSProperties;
  variant?: TabVariant;
  headerHeight?: number;
  headerItemWidth?: number | "auto";
  onClickTab?: (header: string) => void;
  onTriggerExpand?: () => void;
  headersCanBeClicked?: Map<string, boolean>;
  headerStyle?: React.CSSProperties;
  chevronButton?: "none" | "single" | "double";
  chevronButtondegree?: number;
};

export default function TabPanel({
  id,
  style,
  collapsible,
  children,
  defaultSelectedIndex = 0,
  allowChange = true,
  disabled = false,
  activeTabStyle,
  variant = "command",
  headerHeight = 60,
  headerItemWidth,
  onClickTab,
  onTriggerExpand = () => {},
  headersCanBeClicked,
  headerStyle = {},
  chevronButton,
  chevronButtondegree,
}: Props) {
  const headers = Object.keys(children);
  const [activeTab, setActiveTab] = useState(headers[defaultSelectedIndex]);
  var [expanded, setExpanded] = useState(true);
  const [chevronButtonOrientation, setChevronButtonOrientation] = useState(
    chevronButtondegree ?? 0
  );

  const showChevronButton = chevronButton
    ? chevronButton
    : collapsible
    ? "single"
    : "none";

  const onTabClicked = (header: string) => {
    if (headersCanBeClicked !== undefined) {
      if (headersCanBeClicked.has(header)) {
        if (!headersCanBeClicked.get(header)) {
          return;
        }
      }
    }

    if (onClickTab !== undefined) {
      onClickTab(header);
    }

    if (!allowChange) {
      //selected index is set from props, disallow changes
      return;
    }
    if (!expanded) {
      setExpanded(true);
    }

    setActiveTab(header);
  };

  useEffect(() => {
    if (defaultSelectedIndex > -1 && defaultSelectedIndex < headers.length) {
      setActiveTab(headers[defaultSelectedIndex]);
    }
  }, [defaultSelectedIndex, children]);

  useEffect(() => {
    if (chevronButtondegree !== undefined) {
      setChevronButtonOrientation(chevronButtondegree);
    }
  }, [chevronButtondegree]);

  let headerViews = [];
  for (let header of headers) {
    let headerCanBeClicked = true;

    if (headersCanBeClicked !== undefined) {
      headerCanBeClicked = headersCanBeClicked.get(header) ?? true;
    }

    headerViews.push(
      <div
        style={getHeaderStyle(
          headerStyle,
          header === activeTab,
          headerHeight,
          variant,
          headerItemWidth,
          headerCanBeClicked
        )}
        onClick={(event) => onTabClicked(header)}
        key={header}
      >
        {header}
      </div>
    );
  }

  const collapsedStyle = {
    ...style,
    minHeight: 0,
  };

  const expandedStyle = {
    ...style,
  };

  console.log("TAB PANEL RENDERED " + id + " " + defaultSelectedIndex);

  return (
    <div
      id={`TAB_PANEL_${id}`}
      style={expanded ? expandedStyle : collapsedStyle}
    >
      <div
        id={`TAB_PANEL_HEADER_${id}`}
        style={getHeaderContainerStyle(variant, headerItemWidth !== undefined)}
      >
        {headerViews}
        {showChevronButton !== "none" && (
          <div
            onClick={() => {
              if (collapsible) {
                setExpanded(!expanded);
              }
              setChevronButtonOrientation(
                chevronButtonOrientation === 180 ? 0 : 180
              );
              onTriggerExpand();
            }}
            style={{
              marginLeft: "13px",
              marginRight: "11px",
            }}
          >
            {showChevronButton === "single" && (
              <svg
                transform={`rotate(${chevronButtonOrientation})`}
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.375 12.0417L25.5 14.1667L17 22.6667L8.5 14.1667L10.625 12.0417L17 18.4167L23.375 12.0417Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 17C0 26.3889 7.61122 34 17 34C26.3888 34 34 26.3889 34 17C34 7.61115 26.3888 0 17 0C7.61122 0 0 7.61115 0 17ZM17 31.1667C9.17597 31.1667 2.83333 24.824 2.83333 17C2.83333 9.17597 9.17597 2.83333 17 2.83333C24.824 2.83333 31.1667 9.17597 31.1667 17C31.1667 24.824 24.824 31.1667 17 31.1667Z"
                  fill="white"
                />
              </svg>
            )}
            {showChevronButton === "double" && (
              <svg
                transform={`rotate(${chevronButtonOrientation})`}
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.2476 17.0109L10.2287 11.2104C9.81266 10.8095 9.81266 10.1612 10.2287 9.76459L11.2289 8.80069C11.6449 8.39977 12.3176 8.39977 12.7292 8.80069L16.9956 12.9122L21.2619 8.80069C21.678 8.39977 22.3507 8.39977 22.7623 8.80069L23.7713 9.76032C24.1873 10.1612 24.1873 10.8095 23.7713 11.2062L17.7524 17.0067C17.3364 17.4118 16.6636 17.4118 16.2476 17.0109ZM17.7524 25.1998L23.7713 19.3994C24.1873 18.9984 24.1873 18.3502 23.7713 17.9535L22.7711 16.9896C22.3551 16.5887 21.6824 16.5887 21.2708 16.9896L17 21.0969L12.7336 16.9853C12.3176 16.5844 11.6449 16.5844 11.2333 16.9853L10.2287 17.9492C9.81266 18.3502 9.81266 18.9984 10.2287 19.3951L16.2476 25.1956C16.6636 25.6008 17.3364 25.6008 17.7524 25.1998Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M17 34C26.3889 34 34 26.3888 34 17C34 7.61117 26.3889 0 17 0C7.61112 0 0 7.61117 0 17C0 26.3888 7.61112 34 17 34ZM17 31.1667C24.824 31.1667 31.1667 24.824 31.1667 17C31.1667 9.17595 24.824 2.83333 17 2.83333C9.17599 2.83333 2.83333 9.17595 2.83333 17C2.83333 24.824 9.17599 31.1667 17 31.1667Z"
                  fill="white"
                />
              </svg>
            )}
          </div>
        )}
      </div>
      <div
        style={{
          height: `calc(100% - ${headerHeight + 1}px`,
          overflow: "auto",
          padding: expanded ? "7px" : 0,
          position: "relative",
          opacity: disabled ? 0.5 : 1,
          ...activeTabStyle,
        }}
      >
        {expanded && children[activeTab]}
        {disabled && (
          <div
            style={{
              position: "absolute",
              zIndex: 99,
              left: 0,
              top: 0,
              bottom: 0,
              right: 0,
            }}
          />
        )}
      </div>
    </div>
  );
}
